/**
 * Shared permission helpers.
 *
 * Super admin (by email) bypasses every gate. Everyone else may only open a
 * page whose permission key is present in their `accessables` string. The same
 * key model drives the Sidebar links, the route guard, and the landing page,
 * and mirrors the server middleware (requireStatusPermission.js).
 */
import { DEPARTMENTS, pathnameOf, userDeptKeys } from './departments';

export const SUPER_ADMIN_EMAIL = 'immanueldavid@mtandt.com';

export const isSuperAdmin = (user) => user?.email === SUPER_ADMIN_EMAIL;

export const normalizedAccessables = (user) =>
    (user?.accessables || '')
        .split(',')
        .map(a => a.trim().toLowerCase().replace(/\s+/g, ''))
        .filter(Boolean);

export const canChangeMachineStatus = (user) =>
    isSuperAdmin(user) || normalizedAccessables(user).includes('machinestatus');

// Permission key → accepted spellings in `accessables` (covers legacy typos).
export const PAGE_ALIASES = {
    // Analysis replaced the old Overview + Dashboard pages — anyone who could
    // open either of those keeps access without an accessables migration.
    analysis: ['analysis', 'analytics', 'overview', 'dashboard'],
    controlpanel: ['controlpanel', 'control-panel'],
    hardware: ['hardware'],
    devicemanagement: ['devicemanagement', 'device', 'devices'],
    // ONE machine's detail page is not the device LIST: a service engineer must
    // be able to open a machine from their own equipment list without holding
    // the whole Device Management key (and Operations opens it via that key).
    // `rentals` too — the Rental daily report opens a machine's day from every
    // ran / did-not-run / moved / overtime row.
    machinedetail: ['machinedetail', 'devicemanagement', 'service', 'rentals', 'rental', 'renatal'],
    // The service-side machine detail (next service, timeline, log an action).
    servicemachine: ['servicemachine', 'service', 'devicemanagement'],
    deviceconfig: ['deviceconfig', 'device-config'],
    rentals: ['rentals', 'rental', 'renatal'],
    service: ['service'],
    // Field errors (Alarms) are their own key so a department grant can hand
    // them out WITHOUT handing out the Service page — Operations needs Errors,
    // not job cards. 'service' stays an alias, so service users keep access.
    alarms: ['alarms', 'service'],
    // Daily Report is a shared page: EITHER team key opens it, and inside the
    // page each team sees only its own view (rentals → Rental, service → Service).
    dailyreport: ['rentals', 'rental', 'renatal', 'service'],
    billing: ['billing'],
    inventory: ['inventory'],
    // The OEM parts catalogue is reference data, not stock: the service
    // engineer identifying a broken part and the storekeeper ordering it both
    // need it, so both keys open it without either being handed the other's page.
    partscatalog: ['partscatalog', 'parts-catalog', 'parts', 'service', 'inventory'],
    procurement: ['procurement'],
    organization: ['organization'],
    mail: ['mail'],
    emailintake: ['emailintake', 'mailintake', 'intake'],
    chat: ['chat'],
    tasks: ['tasks', 'task'],
    sms: ['sms', 'm2msms'],
    debug: ['debug', 'debugconsole'],
};

// Path → required permission key. Matched by exact path or `path + '/'` prefix
// (so detail/sub pages inherit the parent's permission). Most specific first.
const PAGE_RULES = [
    ['/control-panel', 'controlpanel'],
    // Sinoboom i-Link integration settings — no user holds the 'sinoboom' key,
    // so only the super admin (who bypasses every key) can open it.
    ['/sinoboom-settings', 'sinoboom'],
    ['/hardware', 'hardware'],
    ['/analysis', 'analysis'],
    ['/device-config', 'deviceconfig'],
    ['/equipment', 'devicemanagement'],
    ['/devices', 'devicemanagement'],
    ['/data-management', 'devicemanagement'],
    ['/mewp-detail', 'machinedetail'],
    ['/mewp-data', 'devicemanagement'],
    ['/mewp', 'devicemanagement'],
    ['/battery-packs', 'devicemanagement'],
    ['/tower-lights', 'devicemanagement'],
    ['/geofencing', 'devicemanagement'],
    ['/rentals', 'rentals'],
    ['/rental-details', 'rentals'],
    ['/rental-log', 'rentals'],
    ['/contract', 'rentals'],
    ['/contract-live', 'rentals'],
    ['/contract-data', 'rentals'],
    ['/reports', 'dailyreport'],
    ['/service-machine', 'servicemachine'],     // before /service — most specific first
    ['/service', 'service'],
    ['/parts-catalog', 'partscatalog'],
    ['/alarms', 'alarms'],
    ['/maintenance', 'service'],
    ['/billing', 'billing'],
    ['/inventory', 'inventory'],
    ['/procurement', 'procurement'],
    ['/organization', 'organization'],
    ['/email-intake', 'emailintake'],
    ['/mail', 'mail'],
    ['/chat', 'chat'],
    ['/tasks', 'tasks'],
    ['/sms', 'sms'],
    ['/debug', 'debug'],
    ['/settings', '__superadmin__'],
];

// Personal pages anyone signed in may open; '/' is handled by the index redirect.
// The user guide and the creator's news are app-wide reads (writes on both are
// super-admin-only, enforced server-side).
const ALWAYS_ALLOWED = ['/', '/profile', '/calendar', '/how-to-use', '/creator-news'];

// ─── Department-owned namespaces ────────────────────────────────────────────
// /sales/*, /operations/* and /financial/* are the departments' OWN pages
// (each header mounts its own file — see App.jsx). They are gated on the
// DEPARTMENT itself, never on a page key: only users granted that department
// in `accessables` (and the super admin) may open them, so one department's
// user can never walk into another department's screens. Service's own pages
// live under /service/* which the 'service' key below already rules — for
// service, page key and department grant are the same word, so the gate is
// identical.
const DEPT_NAMESPACE_RE = /^\/(sales|operations|financial)(\/|$)/;

/** The permission key a path requires, or null when it's open to any signed-in user. */
export const pageKeyForPath = (pathname) => {
    if (ALWAYS_ALLOWED.includes(pathname)) return null;
    const dept = DEPT_NAMESPACE_RE.exec(pathname);
    if (dept) return `__dept:${dept[1]}`;
    const rule = PAGE_RULES.find(([prefix]) => pathname === prefix || pathname.startsWith(prefix + '/'));
    return rule ? rule[1] : null;
};

// ─── Department grants ──────────────────────────────────────────────────────
// Putting a DEPARTMENT's name in `accessables` (instead of, or alongside, page
// keys) gives the user that whole department: every page behind its headers,
// and a top bar showing ONLY their department(s). One word per department is
// all an admin has to type.
//
// A department can also be granted HEADER BY HEADER: `<department>/<header>`
// (the header's path after the department prefix, e.g. "operations/log-sheets",
// "service/control-panel") makes the user a member of that department but
// shows and opens ONLY the headers named. The pages reached by clicking inside
// them (the department's `grants`) come with it. A plain department name still
// grants every header.
export const DEPARTMENT_ALIASES = {
    service: ['service'],
    sales: ['sales'],
    operations: ['operations', 'ops', 'operation'],
    financial: ['financial', 'finance', 'accounts'],
    marketing: ['marketing'],
};

const hasWholeDepartment = (acc, key) => (DEPARTMENT_ALIASES[key] || [key]).some(a => acc.includes(a));

/** A department header's slug: "/operations/log-sheets" → "log-sheets". */
const headerSlug = (itemPath) => pathnameOf(itemPath).split('/').filter(Boolean).slice(1).join('/');

/** Department key → Set of header slugs granted one by one ("operations/log-sheets"). */
const scopedHeaders = (user) => {
    const out = {};
    for (const a of normalizedAccessables(user)) {
        const slash = a.indexOf('/');
        if (slash < 1) continue;
        const word = a.slice(0, slash);
        const key = Object.keys(DEPARTMENT_ALIASES).find(k => DEPARTMENT_ALIASES[k].includes(word));
        if (key) (out[key] = out[key] || new Set()).add(a.slice(slash + 1));
    }
    return out;
};

/**
 * Which of a department's headers this user may open: null = all of them
 * (the whole department, or the super admin), else the Set of granted slugs
 * (empty when they are not a member at all).
 */
const departmentHeaderScope = (user, key) => {
    if (isSuperAdmin(user)) return null;
    if (hasWholeDepartment(normalizedAccessables(user), key)) return null;
    return scopedHeaders(user)[key] || new Set();
};

/** Department keys this user was granted in `accessables` (ordered as the bar is). */
export const userDepartments = (user) => {
    const acc = normalizedAccessables(user);
    const scoped = scopedHeaders(user);
    return DEPARTMENTS
        .map(d => d.key)
        .filter(key => hasWholeDepartment(acc, key) || !!scoped[key]);
};

/**
 * Every page-permission key reachable from the user's granted departments:
 * the keys of its headers, plus the department's `grants` — pages reached BY
 * CLICKING inside those headers (a machine's detail page, say) which have no
 * header of their own.
 */
const departmentPageKeys = (user) => {
    const mine = new Set(userDepartments(user));
    const keys = new Set();
    for (const dept of DEPARTMENTS) {
        if (!mine.has(dept.key)) continue;
        const scope = departmentHeaderScope(user, dept.key);
        for (const item of dept.items) {
            if (scope && !scope.has(headerSlug(item.path))) continue;
            const k = pageKeyForPath(pathnameOf(item.path));
            if (k) keys.add(k);
        }
        for (const k of (dept.grants || [])) keys.add(k);
    }
    return keys;
};

/**
 * Does the user hold this key OUTRIGHT — named in `accessables` (or an alias),
 * not merely reached through a department? The Workspace group is filtered on
 * this, so a department-only user sees their department and nothing else.
 */
export const userHasExplicitPage = (user, key) => {
    if (!key) return true;
    if (isSuperAdmin(user)) return true;
    if (key === '__superadmin__') return false;
    const acc = normalizedAccessables(user);
    const aliases = PAGE_ALIASES[key] || [key];
    return aliases.some(a => acc.includes(a));
};

/**
 * Does this user hold a given permission key (super admin holds all)?
 * Either outright, or through a department they were granted.
 * '__dept:<name>' keys (department-owned pages) are held ONLY by that
 * department's members — a page key can never open another department.
 */
export const userHasPage = (user, key) => {
    if (!key) return true;
    if (isSuperAdmin(user)) return true;
    if (key === '__superadmin__') return false;
    if (key.startsWith('__dept:')) return userDepartments(user).includes(key.slice(7));
    if (userHasExplicitPage(user, key)) return true;
    return departmentPageKeys(user).has(key);
};

/**
 * Is this path (or a page under it) a department header that a header-scoped
 * member was NOT granted? Those members are held to the headers named;
 * everyone else is decided by the page key as before.
 */
const outsideHeaderScope = (user, pathname) => {
    for (const dept of DEPARTMENTS) {
        const item = dept.items.find(i => {
            const p = pathnameOf(i.path);
            return pathname === p || pathname.startsWith(p + '/');
        });
        if (!item) continue;
        const scope = departmentHeaderScope(user, dept.key);
        return !!scope && scope.size > 0 && !scope.has(headerSlug(item.path));
    }
    return false;
};

export const canViewPath = (user, pathname) =>
    !outsideHeaderScope(user, pathname) && userHasPage(user, pageKeyForPath(pathname));

// Daily Report team views: rental-team users (rentals key) see the Rental view,
// service-team users (service key) the Service view; super admin sees both.
export const canSeeRentalReport = (user) => userHasPage(user, 'rentals');
export const canSeeServiceReport = (user) => userHasPage(user, 'service');

// Where to send a user when the app opens / they hit a page they can't view.
// Control Panel first (per product decision), then the first page they can open.
const LANDING_ORDER = [
    ['controlpanel', '/control-panel'],
    ['analysis', '/analysis'],
    ['devicemanagement', '/equipment'],
    ['hardware', '/hardware'],
    ['rentals', '/rentals'],
    ['service', '/service'],
    ['billing', '/billing'],
    ['inventory', '/inventory'],
    ['procurement', '/procurement'],
    ['organization', '/organization'],
    ['tasks', '/tasks'],
    ['mail', '/mail'],
    ['chat', '/chat'],
    ['debug', '/debug'],
];

export const landingPath = (user) => {
    if (isSuperAdmin(user)) return '/control-panel';
    // A department user lands inside their own department (the profile's
    // department first, as the bar is ordered), on its first header they hold.
    const mine = userDepartments(user);
    const order = [...new Set([...userDeptKeys(user).filter(k => mine.includes(k)), ...mine])];
    for (const key of order) {
        const dept = DEPARTMENTS.find(d => d.key === key);
        const item = dept?.items.find(i => canViewPath(user, pathnameOf(i.path)));
        if (item) return item.path;
    }
    const hit = LANDING_ORDER.find(([key]) => userHasPage(user, key));
    return hit ? hit[1] : '/profile';
};
